import { useLocalStorage } from "hooks/useLocalStorage";
import React from "react";
import { axiosClient } from "api/api";

export type AuthData = {
  role?: number;
  email: string;
  access_token: string;
  display_name: string;
  username: string;
  panelistId: number;
  id: number;
  date: number;
  name: string;
  user_country: any;
  affiliate: any;
  isFlagVirtualWallet: boolean;
  isVirtualWalletOpen: boolean;
  subsCount: number;
  profile_pic?: string;
  isGuestUser?: number;
  qual_popup_shown?: number;
};
export type AuthContextType = {
  roleLoading?: boolean;
  auth: AuthData | null;
  setAuth: (auth: AuthData) => void;
};
const initialValue: AuthContextType = {
  auth: null,
  setAuth: () => {
    //Just empty body
  },
};

const AuthContext = React.createContext<AuthContextType>(initialValue);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { value, setValue } = useLocalStorage("auth", null);
  const [resolvedRoleUser, setResolvedRoleUser] = React.useState<number | null>(null);
  const userId = value?.id;

  React.useEffect(() => {
    if (!userId) return;
    let active = true;
    let pending = false;
    const refreshRole = async () => {
      if (pending) return;
      pending = true;
      try {
        const response = await axiosClient.get('/api/user/session-role');
        if (active) {
          const role = Number(response.data.result.role) === 2 ? 2 : 1;
          setValue(current => current?.id === userId ? { ...current, role } : current);
        }
      } catch {
        // Fail closed for admin UI; focus/reload retries the role lookup.
        if (active) setValue(current => current?.id === userId ? { ...current, role: 1 } : current);
      } finally {
        pending = false;
        if (active) setResolvedRoleUser(userId);
      }
    };
    void refreshRole();
    window.addEventListener('focus', refreshRole);
    return () => { active = false; window.removeEventListener('focus', refreshRole); };
  }, [userId, setValue]);

  const setAuth = (auth: AuthData) => {
    setValue(auth);
  };

  return <AuthContext.Provider value={{ auth: value, setAuth, roleLoading: !!userId && resolvedRoleUser !== userId }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => React.useContext(AuthContext);
